// ErrorLandingPage.js

import React from "react";
import { Box, Typography, Button, Container, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import BarChartIcon from "@mui/icons-material/BarChart";

export default function ErrorLandingPage({ onRetry }) {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 6 } }}>
      <Box
        sx={{
          textAlign: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            background: "linear-gradient(90deg, #39d0ff, #6f5df5)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 2,
            fontSize: { xs: "2rem", sm: "3rem" },
          }}
        >
          Welcome to Premier League Scorigami
        </Typography>
        
        <Typography
          variant="h6"
          sx={{ color: "#b0b0b0", mb: 3, maxWidth: 700, mx: "auto" }}
        >
          We're experiencing temporary difficulty loading the latest match data. 
          While we work to restore the connection, explore what makes Premier League 
          Scorigami unique.
        </Typography>

        <Button
          variant="contained"
          onClick={onRetry}
          sx={{
            background: "linear-gradient(90deg, #39d0ff, #6f5df5)",
            color: "#fff",
            px: 4,
            py: 1.5,
            fontSize: "1.1rem",
            "&:hover": {
              background: "linear-gradient(90deg, #6f5df5, #39d0ff)",
            },
          }}
        >
          Retry Loading Data
        </Button>
      </Box>

      <Divider sx={{ my: 4, borderColor: "rgba(111,93,245,0.3)" }} />

      {/* What is Scorigami Section */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <SportsScoreIcon sx={{ color: "#39d0ff", fontSize: 32 }} />
          <Typography variant="h4" sx={{ color: "#39d0ff" }}>
            What is Scorigami?
          </Typography>
        </Box>
        
        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          Scorigami is a fascinating concept that tracks unique final scores in sports. When a 
          specific scoreline occurs for the very first time in a competition's history, it becomes 
          a "scorigami" - a score that has never been achieved before. The term was popularized by 
          Jon Bois in his acclaimed video series about the NFL, where he meticulously documented 
          every unique final score in American football history.
        </Typography>
        
        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          Premier League Scorigami brings this captivating analysis to English football, visualizing 
          over 30 years of match results through an interactive heatmap. Since the Premier League's 
          inception in 1992, more than 11,000 matches have been played, producing hundreds of unique 
          scorelines. Some scores like 1-0 and 2-1 occur frequently, while others - like 7-4 or 8-2 - 
          happen only once in history, making them legendary moments in football folklore.
        </Typography>
      </Box>

      <Divider sx={{ my: 4, borderColor: "rgba(111,93,245,0.3)" }} />

      {/* How It Works Section */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <BarChartIcon sx={{ color: "#39d0ff", fontSize: 32 }} />
          <Typography variant="h4" sx={{ color: "#39d0ff" }}>
            How the Heatmap Works
          </Typography>
        </Box>
        
        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          Our interactive heatmap uses a sophisticated visualization system to display every possible 
          scoreline. The x-axis (horizontal) represents the winning team's score, while the y-axis 
          (vertical) shows the losing team's score. Each cell in the grid represents a specific 
          final score that could occur in a Premier League match.
        </Typography>
        
        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          <strong style={{ color: "#6f5df5" }}>Color intensity tells the story:</strong> Brighter, 
          more vibrant colors indicate scores that have occurred multiple times throughout Premier 
          League history. Common results like 2-1 or 1-0 glow brightly, having occurred hundreds of 
          times. Dimmer cells represent rare scorelines - perhaps occurring only once or twice in 
          three decades. Empty cells are scores that have never happened, representing opportunities 
          for future scorigamis.
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          <strong style={{ color: "#6f5df5" }}>Interactive exploration:</strong> When the data loads, 
          you'll be able to click any cell to see the exact matches that produced that score, including 
          the teams involved, dates played, and whether it was a historic first occurrence. A toggle
          switches between home-versus-away goals and the classic winner-versus-loser count.
        </Typography>
      </Box>

      <Divider sx={{ my: 4, borderColor: "rgba(111,93,245,0.3)" }} />

      {/* Why This Matters Section */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <TrendingUpIcon sx={{ color: "#39d0ff", fontSize: 32 }} />
          <Typography variant="h4" sx={{ color: "#39d0ff" }}>
            Why Scorigami Matters
          </Typography>
        </Box>
        
        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          Football is a sport defined by its unpredictability and drama. Unlike basketball or American 
          football where scores often reach triple digits, football's low-scoring nature means each 
          goal carries immense weight. This scarcity of goals makes the distribution of final scores 
          particularly interesting from a statistical perspective.
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          <strong style={{ color: "#6f5df5" }}>Historical patterns emerge:</strong> By analyzing 
          30+ years of Premier League data, we can identify fascinating trends. The most common 
          scoreline, 1-0, reflects football's defensive nature and the value of a single goal. 
          As scores increase, they become exponentially rarer. A 5-4 thriller represents not just 
          nine goals, but an exceptional match where both teams played attacking football and defenses 
          struggled - a perfect storm of circumstances.
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          <strong style={{ color: "#6f5df5" }}>Tactical evolution:</strong> Scorigami analysis reveals 
          how football tactics have evolved. Early Premier League seasons featured different playing 
          styles compared to today's data-driven, possession-based approach. By comparing different 
          seasons, you can see these tactical shifts reflected in the distribution of scorelines.
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          <strong style={{ color: "#6f5df5" }}>Memorable moments:</strong> Every rare scorigami 
          represents a memorable match. When Southampton lost 9-0 to Leicester City in 2019 (and 
          later 9-0 to Manchester United), it wasn't just a heavy defeat - it was history being 
          made. These extreme results become part of football's rich tapestry of stories, talked 
          about for years and preserved in our visualization.
        </Typography>
      </Box>

      <Divider sx={{ my: 4, borderColor: "rgba(111,93,245,0.3)" }} />

      {/* Features Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" sx={{ color: "#39d0ff", mb: 2 }}>
          Features You'll Find Here
        </Typography>

        <Box sx={{ ml: 2 }}>
          <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
            <strong style={{ color: "#6f5df5" }}>Season-by-Season Analysis:</strong> Use our dropdown 
            menu to filter by specific Premier League seasons or view cumulative all-time data. Compare 
            how the 1992/93 inaugural season differs from the modern game, or track how the current 
            season is unfolding.
          </Typography>

          <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
            <strong style={{ color: "#6f5df5" }}>Interactive Details:</strong> Every cell in the 
            heatmap is clickable. Discover which teams produced each scoreline, when it first occurred 
            (the true scorigami moment), and whether it has been repeated since. For single-season views, 
            see every match that achieved that particular score.
          </Typography>

          <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
            <strong style={{ color: "#6f5df5" }}>Desktop Hover Previews:</strong> On desktop computers, 
            simply hover over any cell to see quick statistics including occurrence count, first 
            appearance date, and the teams involved - all without clicking.
          </Typography>

          <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
            <strong style={{ color: "#6f5df5" }}>Mobile-Optimized:</strong> The heatmap scales 
            intelligently to fit any screen size, ensuring you can explore Premier League history 
            whether you're on a phone, tablet, or desktop computer.
          </Typography>

          <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
            <strong style={{ color: "#6f5df5" }}>Regular Updates:</strong> Our database refreshes 
            regularly throughout each season, typically within 24-48 hours of matches being played. 
            This means you can track potential new scorigamis as they happen each matchday.
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 4, borderColor: "rgba(111,93,245,0.3)" }} />

      {/* The Data Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" sx={{ color: "#39d0ff", mb: 2 }}>
          About Our Data
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          Premier League Scorigami draws from comprehensive official Premier League records spanning 
          from August 1992 to the present day. This represents every competitive match in the modern 
          Premier League era - over 11,000 matches featuring 49 different clubs that have competed 
          in England's top flight.
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          The Premier League began in 1992 when the First Division clubs resigned from the Football 
          League to form an independent competition. While this means our dataset doesn't include 
          the old First Division era (pre-1992), it captures the entire modern history of English 
          top-flight football as we know it today, including the evolution from 22 teams to the 
          current 20-team format.
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          Our analysis includes all 380 matches played each season (38 matchdays with 10 matches each), 
          covering every possible pairing of teams home and away. This comprehensive dataset allows 
          for meaningful statistical analysis of scoring patterns, tactical trends, and historical 
          anomalies.
        </Typography>
      </Box>

      <Divider sx={{ my: 4, borderColor: "rgba(111,93,245,0.3)" }} />

      {/* Interesting Facts Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" sx={{ color: "#39d0ff", mb: 2 }}>
          Did You Know?
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          📊 <strong style={{ color: "#6f5df5" }}>Most Common Score:</strong> 1-0 is historically 
          the most frequent Premier League result, occurring in approximately 10% of all matches. 
          This reflects football's inherently defensive nature and the difficulty of scoring goals 
          at the highest level.
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          🎯 <strong style={{ color: "#6f5df5" }}>The Sweet Spot:</strong> Scores between 0-4 goals 
          for each team account for over 95% of all Premier League matches. Results beyond this range 
          are statistical outliers representing extraordinary circumstances.
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          🔥 <strong style={{ color: "#6f5df5" }}>High-Scoring Rarity:</strong> Matches with 10 or 
          more total goals are exceptionally rare in Premier League history, with only a handful of 
          such encounters in three decades. Portsmouth's 7-4 victory over Reading in 2007 remains one 
          of the most memorable high-scoring affairs.
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          🏆 <strong style={{ color: "#6f5df5" }}>Record Margin:</strong> Two separate 9-0 victories 
          involving Southampton (losing to Leicester in 2019 and Manchester United in 2021) represent 
          the largest winning margin in Premier League history - a scoreline that had never occurred 
          before the 2019/20 season.
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          ⚽ <strong style={{ color: "#6f5df5" }}>Draw Distribution:</strong> Scoreless draws (0-0) 
          account for roughly 3-4% of all matches, while 1-1 draws are slightly more common at around 
          5-6%. High-scoring draws like 4-4 or 5-5 are incredibly rare and memorable events.
        </Typography>
      </Box>

      <Divider sx={{ my: 4, borderColor: "rgba(111,93,245,0.3)" }} />

      {/* Navigation Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" sx={{ color: "#39d0ff", mb: 2 }}>
          Explore More
        </Typography>

        <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
          While you wait for the match data to load, explore other sections of our site using the 
          menu button in the top-left corner:
        </Typography>

        <Box sx={{ ml: 2 }}>
          <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
            📖 <strong style={{ color: "#6f5df5" }}>About Page:</strong> Dive deeper into the 
            methodology behind our analysis and learn more about how to interpret the heatmap 
            visualization effectively.
          </Typography>

          <Typography paragraph sx={{ color: "#b0b0b0", lineHeight: 1.8, fontSize: "1.05rem" }}>
            ❓ <strong style={{ color: "#6f5df5" }}>FAQ:</strong> Find answers to common questions 
            about Premier League scoring patterns, rare scorelines, and how to use our interactive 
            features.
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 4, borderColor: "rgba(111,93,245,0.3)" }} />

      {/* Call to Action */}
      <Box sx={{ textAlign: "center", mt: 6, mb: 4 }}>
        <Typography
          variant="h5"
          sx={{ color: "#39d0ff", mb: 2 }}
        >
          Ready to Explore Premier League History?
        </Typography>
        
        <Typography
          paragraph
          sx={{ color: "#b0b0b0", mb: 3, maxWidth: 600, mx: "auto" }}
        >
          Click the button below to retry loading the interactive heatmap and start 
          discovering unique scores from over 30 years of Premier League football.
        </Typography>

        <Button
          variant="contained"
          onClick={onRetry}
          sx={{
            background: "linear-gradient(90deg, #39d0ff, #6f5df5)",
            color: "#fff",
            px: 5,
            py: 1.5,
            fontSize: "1.1rem",
            "&:hover": {
              background: "linear-gradient(90deg, #6f5df5, #39d0ff)",
            },
          }}
        >
          Retry Loading Data
        </Button>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="text"
            onClick={() => navigate("/about")}
            sx={{
              color: "#b0b0b0",
              mr: 2,
              "&:hover": { color: "#39d0ff" },
            }}
          >
            Learn More
          </Button>
          <Button
            variant="text"
            onClick={() => navigate("/faq")}
            sx={{
              color: "#b0b0b0",
              "&:hover": { color: "#39d0ff" },
            }}
          >
            View FAQ
          </Button>
        </Box>
      </Box>
    </Container>
  );
}